import React, { useMemo } from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

// 伪随机（基于种子，渲染期稳定）
const seeded = (seed: number) => {
  const x = Math.sin(seed * 9301 + 49297) * 233280;
  return x - Math.floor(x);
};

// 生成更自然的山脊线（多倍频噪声 + 多采样点）
const buildRidgePath = (
  width: number,
  peakHeight: number,
  baseline: number,
  seed: number,
  samples = 48,
) => {
  const pts: Array<[number, number]> = [];
  for (let i = 0; i <= samples; i++) {
    const x = (i / samples) * width * 2;
    const n1 = seeded(seed + i * 1.3);
    const n2 = seeded(seed + i * 0.37 + 11);
    const n3 = seeded(seed + i * 0.11 + 23);
    const n = n1 * 0.55 + n2 * 0.3 + n3 * 0.15;
    const y = baseline - peakHeight * (0.25 + n * 0.75);
    pts.push([x, y]);
  }
  const d =
    `M 0 ${baseline + 120} L 0 ${pts[0][1]} ` +
    pts
      .slice(1)
      .map(([x, y]) => `L ${x.toFixed(1)} ${y.toFixed(1)}`)
      .join(' ') +
    ` L ${width * 2} ${baseline + 120} Z`;
  return { d, points: pts };
};

// 雪线路径（只画每段顶部、在 snowLine 之上的白色雪帽）
const buildSnowPath = (
  points: Array<[number, number]>,
  snowLine: number,
) => {
  let d = '';
  let inSnow = false;
  for (let i = 0; i < points.length; i++) {
    const [x, y] = points[i];
    if (y <= snowLine) {
      if (!inSnow) {
        d += `M ${x.toFixed(1)} ${snowLine} L ${x.toFixed(1)} ${y.toFixed(1)} `;
        inSnow = true;
      } else {
        d += `L ${x.toFixed(1)} ${y.toFixed(1)} `;
      }
    } else {
      if (inSnow) {
        d += `L ${x.toFixed(1)} ${snowLine} Z `;
        inSnow = false;
      }
    }
  }
  if (inSnow) {
    const lastX = points[points.length - 1][0];
    d += `L ${lastX.toFixed(1)} ${snowLine} Z`;
  }
  return d;
};

export const ParallaxMountains: React.FC<{
  title?: string;
  subtitle?: string;
}> = ({ title = '🏔️ 山脉视差', subtitle = '多层视差 · 日出渐变 · 云雾 · 飞鸟' }) => {
  const frame = useCurrentFrame();
  const { width, height, fps, durationInFrames } = useVideoConfig();

  // 日出进度（驱动天空色 + 太阳位置 + 星星淡出）
  const sunrise = interpolate(frame, [0, fps * 3.5], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  // 视差滚动（spring 缓出）
  const scrollProgress = spring({
    frame,
    fps,
    config: { damping: 22, stiffness: 70, mass: 1.1 },
  });

  // 5 层山脉（远→近）：颜色随日出推进由冷→暖
  const layers = useMemo(
    () =>
      [
        { speed: 0.08, base: 260, peak: 220, color: ['#1d2640', '#404a66'], offset: 0, seed: 7, fog: 0.55, snow: true },
        { speed: 0.16, base: 260, peak: 230, color: ['#2b3a5a', '#566b8f'], offset: 50, seed: 21, fog: 0.38, snow: true },
        { speed: 0.28, base: 260, peak: 210, color: ['#3d4f75', '#6d84a8'], offset: 110, seed: 44, fog: 0.22, snow: false },
        { speed: 0.45, base: 260, peak: 190, color: ['#2f3c58', '#3a4b6d'], offset: 180, seed: 68, fog: 0.1, snow: false },
        { speed: 0.7, base: 260, peak: 160, color: ['#1a2238', '#232c44'], offset: 260, seed: 95, fog: 0, snow: false },
      ].map((l) => {
        const c1 = l.color[0];
        const c2 = l.color[1];
        const mixed = sunrise < 0.5 ? c1 : c2;
        return { ...l, color: mixed };
      }),
    [sunrise],
  );

  // 太阳
  const sunY = interpolate(sunrise, [0, 1], [height * 0.78, height * 0.35]);
  const sunScale = interpolate(sunrise, [0, 1], [0.7, 1]);
  const sunHalo = 0.35 + Math.sin(frame * 0.08) * 0.05;
  const sunPulse = 1 + Math.sin(frame * 0.12) * 0.02;

  // 星星（日出后消失）
  const stars = useMemo(
    () =>
      Array.from({ length: 60 }, (_, i) => ({
        x: seeded(i + 1) * width,
        y: seeded(i + 101) * height * 0.55,
        size: 0.6 + seeded(i + 201) * 1.4,
        twinkle: seeded(i + 301),
      })),
    [width, height],
  );
  const starOpacity = interpolate(sunrise, [0, 0.45], [0.9, 0], { extrapolateRight: 'clamp' });

  // 云朵：多椭圆叠加，循环平移
  const clouds = useMemo(
    () => [
      { x: width * 0.12, y: height * 0.14, speed: 0.35, size: 1.1 },
      { x: width * 0.55, y: height * 0.08, speed: 0.22, size: 0.9 },
      { x: width * 0.82, y: height * 0.18, speed: 0.45, size: 1.3 },
      { x: width * 0.32, y: height * 0.22, speed: 0.3, size: 0.75 },
    ],
    [width, height],
  );

  // 鸟群：三只鸟，沿余弦曲线飞过
  const birds = useMemo(() => [0, 1, 2].map((i) => ({ i, delay: i * 12 })), []);

  // 前景萤火粒子：升起 + 水平缓漂
  const particleCount = 36;
  const particles = useMemo(
    () =>
      Array.from({ length: particleCount }, (_, i) => {
        const lifetime = 120 + seeded(i + 500) * 80;
        const delay = seeded(i + 600) * lifetime;
        return {
          i,
          baseX: seeded(i + 700) * width,
          drift: (seeded(i + 800) - 0.5) * 60,
          lifetime,
          delay,
          size: 1.2 + seeded(i + 900) * 2.2,
        };
      }),
    [width],
  );

  // 标题弹入
  const titleProgress = spring({
    frame: frame - fps * 0.6,
    fps,
    config: { damping: 14, stiffness: 120 },
  });
  const titleOpacity = interpolate(frame, [fps * 0.6, fps * 1.2], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });
  const titleExit = interpolate(
    frame,
    [durationInFrames - fps * 0.8, durationInFrames],
    [1, 0],
    { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
  );

  // 天空渐变（随日出推进）
  const skyTop = sunrise < 0.5 ? '#0b1028' : '#274b75';
  const skyMid = sunrise < 0.5 ? '#1a2f56' : '#ff9966';
  const skyBottom = sunrise < 0.5 ? '#2f4e7a' : '#ffd9a8';

  return (
    <AbsoluteFill
      style={{
        background: `linear-gradient(to bottom, ${skyTop} 0%, ${skyMid} 55%, ${skyBottom} 100%)`,
        overflow: 'hidden',
      }}
    >
      {/* 星星 */}
      <svg width={width} height={height * 0.6} style={{ position: 'absolute', opacity: starOpacity }}>
        {stars.map((s, i) => {
          const tw = 0.5 + Math.sin(frame * 0.1 + s.twinkle * 10) * 0.5;
          return <circle key={i} cx={s.x} cy={s.y} r={s.size} fill="#ffffff" opacity={0.3 + tw * 0.6} />;
        })}
      </svg>

      {/* 太阳 + 光晕 */}
      <div
        style={{
          position: 'absolute',
          left: width / 2 - 90,
          top: sunY - 90,
          width: 180,
          height: 180,
          transform: `scale(${sunScale * sunPulse})`,
        }}
      >
        <div
          style={{
            position: 'absolute',
            inset: -60,
            borderRadius: '50%',
            background: `radial-gradient(circle, rgba(255, 210, 140, ${sunHalo}) 0%, rgba(255, 180, 100, ${sunHalo * 0.4}) 40%, rgba(255, 180, 100, 0) 70%)`,
            filter: 'blur(6px)',
          }}
        />
        <div
          style={{
            width: '100%',
            height: '100%',
            background: 'radial-gradient(circle at 50% 45%, #fff4cc 0%, #ffd27a 45%, #ff9a4d 100%)',
            borderRadius: '50%',
            boxShadow:
              '0 0 60px rgba(255, 180, 100, 0.7), 0 0 120px rgba(255, 160, 80, 0.45)',
          }}
        />
      </div>

      {/* 云朵 */}
      {clouds.map((cloud, i) => {
        const wrap = width + 200;
        const cloudX = ((cloud.x + frame * cloud.speed) % wrap + wrap) % wrap - 100;
        const baseW = 120 * cloud.size;
        const baseH = 34 * cloud.size;
        const tint = sunrise > 0.5 ? 'rgba(255, 236, 220, 0.9)' : 'rgba(200, 210, 240, 0.6)';
        return (
          <svg
            key={i}
            width={baseW * 2.5}
            height={baseH * 2.5}
            style={{ position: 'absolute', left: cloudX, top: cloud.y }}
          >
            <g fill={tint}>
              <ellipse cx={baseW * 0.4} cy={baseH * 1.1} rx={baseW * 0.4} ry={baseH * 0.55} />
              <ellipse cx={baseW * 0.75} cy={baseH * 0.9} rx={baseW * 0.5} ry={baseH * 0.75} />
              <ellipse cx={baseW * 1.15} cy={baseH * 1.05} rx={baseW * 0.45} ry={baseH * 0.6} />
              <ellipse cx={baseW * 1.5} cy={baseH * 1.15} rx={baseW * 0.35} ry={baseH * 0.5} />
            </g>
          </svg>
        );
      })}

      {/* 鸟群 */}
      <svg width={width} height={height * 0.5} style={{ position: 'absolute', left: 0, top: 0 }}>
        {birds.map((b) => {
          const t = (frame - b.delay) / fps;
          if (t < 0) return null;
          const bx = -50 + t * 80;
          const by = height * 0.22 + Math.sin(t * 2.2 + b.i) * 18;
          if (bx > width + 50) return null;
          const flap = Math.sin(frame * 0.4 + b.i * 1.3) * 6;
          return (
            <path
              key={b.i}
              d={`M ${bx} ${by} Q ${bx + 10} ${by - 6 - flap} ${bx + 20} ${by} Q ${bx + 30} ${by - 6 - flap} ${bx + 40} ${by}`}
              stroke="#1a1a1a"
              strokeWidth={2}
              fill="none"
              opacity={0.75}
            />
          );
        })}
      </svg>

      {/* 山脉层（含雾气 + 雪顶） */}
      {layers.map((layer, i) => {
        const xOffset = scrollProgress * layer.speed * width;
        const { d, points } = buildRidgePath(
          width,
          layer.peak,
          layer.base + layer.offset,
          layer.seed,
        );
        const snowLine = layer.base + layer.offset - layer.peak * 0.55;
        const snowD = layer.snow ? buildSnowPath(points, snowLine) : '';
        const fogOpacity = layer.fog * (1 - sunrise * 0.35);
        const svgHeight = layer.base + layer.peak + 200;

        return (
          <svg
            key={i}
            width={width * 2}
            height={svgHeight}
            style={{
              position: 'absolute',
              bottom: layer.offset,
              left: -xOffset,
            }}
          >
            <defs>
              <linearGradient id={`mountain-${i}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={layer.color} stopOpacity={1} />
                <stop offset="100%" stopColor={layer.color} stopOpacity={0.85} />
              </linearGradient>
            </defs>
            <path d={d} fill={`url(#mountain-${i})`} />
            {layer.snow && snowD && (
              <path d={snowD} fill="rgba(255, 255, 255, 0.9)" opacity={0.85} />
            )}
            {/* 雾气遮罩 */}
            {fogOpacity > 0.02 && (
              <rect
                x={0}
                y={layer.base + layer.offset - layer.peak * 0.3}
                width={width * 2}
                height={200}
                fill="rgba(255, 255, 255, 1)"
                opacity={fogOpacity}
              />
            )}
          </svg>
        );
      })}

      {/* 前景萤火粒子 */}
      <svg width={width} height={height} style={{ position: 'absolute', pointerEvents: 'none' }}>
        {particles.map((p) => {
          const t = ((frame + p.delay) % p.lifetime) / p.lifetime;
          const x = p.baseX + p.drift * t;
          const y = height - 30 - t * 220;
          const alpha = Math.sin(t * Math.PI) * 0.8;
          return (
            <circle
              key={p.i}
              cx={x}
              cy={y}
              r={p.size}
              fill="#fff2b8"
              opacity={alpha * 0.9}
              style={{ filter: 'blur(0.6px)' }}
            />
          );
        })}
      </svg>

      {/* 前景柔光（加深景深） */}
      <div
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          height: 200,
          background: 'linear-gradient(to top, rgba(0, 0, 0, 0.35), rgba(0, 0, 0, 0))',
          pointerEvents: 'none',
        }}
      />

      {/* 中心文字 */}
      <div
        style={{
          position: 'absolute',
          top: height / 2 - 90,
          left: 0,
          right: 0,
          textAlign: 'center',
          zIndex: 10,
          opacity: titleOpacity * titleExit,
          transform: `translateY(${(1 - titleProgress) * 30}px) scale(${0.85 + titleProgress * 0.15})`,
        }}
      >
        <div
          style={{
            fontSize: 76,
            fontWeight: 800,
            color: '#ffffff',
            letterSpacing: 2,
            textShadow:
              '0 4px 20px rgba(0, 0, 0, 0.45), 0 0 40px rgba(255, 200, 140, 0.35)',
          }}
        >
          {title}
        </div>
        <div
          style={{
            marginTop: 16,
            fontSize: 24,
            color: 'rgba(255, 255, 255, 0.88)',
            letterSpacing: 6,
            textShadow: '0 2px 8px rgba(0, 0, 0, 0.4)',
          }}
        >
          {subtitle}
        </div>
      </div>

      {/* 底部信息 */}
      <div
        style={{
          position: 'absolute',
          bottom: 24,
          left: 0,
          right: 0,
          textAlign: 'center',
          fontSize: 24,
          color: 'rgba(255, 255, 255, 0.7)',
          letterSpacing: 2,
        }}
      >
        5 层视差 · 雪顶高山 · 云雾景深 · 飞鸟日出
      </div>
    </AbsoluteFill>
  );
};
